/* Tab button with WAI-ARIA tab semantics: role="tab", aria-selected, aria-controls, managed tabIndex.
   Parent must provide role="tablist" with keyboard handling for Left/Right/Home/End.
   The `secondary` variant mutes the idle text one step (text-slate-400) for use in
   the overflow row of the adaptive contract tab bar, where the row reads as a
   lower-priority continuation of the primary row.
   Labels never wrap (whitespace-nowrap): in the adaptive tab bar a too-long label
   forces the tab into the overflow row rather than growing the row height, and it
   keeps measured widths deterministic. */
import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { clsx } from 'clsx';

interface TabButtonProps extends ButtonHTMLAttributes<HTMLButtonElement> {
  active: boolean;
  icon?: React.ReactNode;
  badge?: number | string;
  panelId: string;
  variant?: 'primary' | 'secondary';
}

const TabButton = forwardRef<HTMLButtonElement, TabButtonProps>(
  (
    { active, icon, badge, panelId, variant = 'primary', children, className, ...props },
    ref
  ) => {
    return (
      <button
        ref={ref}
        role="tab"
        aria-selected={active}
        aria-controls={panelId}
        id={`${panelId}-tab`}
        tabIndex={active ? 0 : -1}
        className={clsx(
          'flex items-center whitespace-nowrap py-3 px-1 border-b-2 text-body-sm font-medium transition-colors',
          active
            ? 'border-brand-500 text-brand-600 dark:text-brand-400 dark:border-brand-400'
            : clsx(
                'border-transparent hover:text-slate-700 hover:border-slate-300 dark:hover:text-slate-300',
                variant === 'secondary'
                  ? 'text-slate-400 dark:text-slate-500'
                  : 'text-slate-500 dark:text-slate-400'
              ),
          className
        )}
        {...props}
      >
        {icon && <span className="mr-2">{icon}</span>}
        {children}
        {badge !== undefined && (
          <span className="ml-2 px-2 py-0.5 text-caption rounded-full bg-slate-100 text-slate-600 dark:bg-slate-700 dark:text-slate-300">
            {badge}
          </span>
        )}
      </button>
    );
  }
);

TabButton.displayName = 'TabButton';

export default TabButton;
